import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { HubBotAvatar } from '@/components/hub-bot-avatar'
import {
  BubbleMotionContext,
  EXIT_MS,
  exitStyle,
  type BubbleMotion,
  type BubbleSnapshot,
} from '@/lib/chat/bubble-motion'
import { prefersReducedMotion } from '@/lib/reduced-motion'

/**
 * Lives in the project layout (board zsy4, round 3): remembers which view has
 * a live HUB bubble, so a remount for the next email / document in the same
 * view keeps the avatar in place, and draws the exit after the route's bubble
 * has already unmounted — a clone at the last known spot sliding out through
 * the nearest edge, gone after EXIT_MS. Navigation is never held up; if the
 * next view mounts a bubble meanwhile, its entrance simply plays alongside.
 */

interface LiveBubble {
  view: string
  instance: string
  entered: boolean
  snapshot: BubbleSnapshot | null
}

interface ExitGhost extends BubbleSnapshot {
  id: number
}

export function BubbleMotionProvider({ children }: { children: ReactNode }) {
  const live = useRef<LiveBubble | null>(null)
  const pending = useRef<{ bubble: LiveBubble; timer: number } | null>(null)
  const [ghost, setGhost] = useState<ExitGhost | null>(null)
  const nextId = useRef(0)

  const motion = useMemo<BubbleMotion>(() => {
    const leave = (bubble: LiveBubble) => {
      if (live.current === bubble) live.current = null
      const snapshot = bubble.snapshot
      if (snapshot?.visible && !prefersReducedMotion())
        setGhost({ id: ++nextId.current, ...snapshot })
    }
    const flushPending = () => {
      const p = pending.current
      if (!p) return
      pending.current = null
      clearTimeout(p.timer)
      leave(p.bubble)
    }
    return {
      register(view, instance) {
        const p = pending.current
        if (p && p.bubble.view === view) {
          // The same view, next subject: keep the avatar where it is.
          pending.current = null
          clearTimeout(p.timer)
          live.current = {
            ...p.bubble,
            instance,
            entered: p.bubble.instance === instance && p.bubble.entered,
          }
          return live.current.entered
        }
        flushPending()
        const cur = live.current
        if (cur && cur.view === view) {
          if (cur.instance !== instance) live.current = { ...cur, instance, entered: false }
          return live.current!.entered
        }
        live.current = { view, instance, entered: true, snapshot: null }
        return true
      },
      update(view, instance, snapshot) {
        const cur = live.current
        if (cur && cur.view === view && cur.instance === instance) cur.snapshot = snapshot
      },
      unregister(view, instance) {
        const cur = live.current
        if (!cur || cur.view !== view || cur.instance !== instance) return
        flushPending()
        pending.current = {
          bubble: cur,
          timer: window.setTimeout(() => {
            pending.current = null
            leave(cur)
          }, 0),
        }
      },
    }
  }, [])

  useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current.timer)
    },
    []
  )

  return (
    <BubbleMotionContext.Provider value={motion}>
      {children}
      {ghost && (
        <BubbleExit
          key={ghost.id}
          ghost={ghost}
          onDone={() => setGhost((g) => (g?.id === ghost.id ? null : g))}
        />
      )}
    </BubbleMotionContext.Provider>
  )
}

function BubbleExit({ ghost, onDone }: { ghost: ExitGhost; onDone: () => void }) {
  const [out, setOut] = useState(false)
  const done = useRef(onDone)
  done.current = onDone
  useEffect(() => {
    const frame = requestAnimationFrame(() => setOut(true))
    const timer = window.setTimeout(() => done.current(), EXIT_MS + 50)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [])
  return (
    <div
      data-bubble-exit={out ? 'out' : 'start'}
      className="pointer-events-none fixed z-40"
      style={{
        left: ghost.pos.x,
        top: ghost.pos.y,
        width: ghost.size,
        height: ghost.size,
        ...exitStyle(ghost.pos, ghost.size, out),
      }}
    >
      <HubBotAvatar palette={ghost.palette} size={ghost.size} state="neutral" />
    </div>
  )
}
